import React, { useContext, memo, useMemo, useCallback } from "react";
import { ProfileContext } from "../context/ProfileContext";
import {
  IonCard,
  IonCardHeader,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonAvatar,
  IonIcon,
  IonButton,
  IonBadge,
  IonList,
  IonText,
} from "@ionic/react";
import { WerkerJobContext } from "../context/JobContextWerker";
import { banOutline, cashOutline, chatboxOutline } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../theme/AppStyles.css";
import { WERK_SEGMENT_SELECTOR, WORKFLOW_WERKER_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { format } from "date-fns";
import {
  getStartTs,
  getHoursText,
  createWerkerPaymentFilter,
  createWerkerStatusFilter,
  createWerkerSearchFilter,
  applySortMode,
  type PaymentFilter,
  type StatusFilter,
  type SortMode,
} from "../utils/jobCardFilters";

const EmptyMessage: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div style={{ padding: "14px 16px", opacity: 0.85 }}>
      <h4 style={{ margin: 0 }}>{text}</h4>
    </div>
  );
};

type Props = {
  jobCardSearchText: string;
  segmentSelection: string;
  PaidStatus: any;
  viewOptions?: { payment: PaymentFilter; status: StatusFilter; sort: SortMode };
  onBeforeNavigate?: () => Promise<void> | void;
  isListInteracting?: boolean;
  onJobSelect?: (jobDocId: string, uniqueJobId: string, schedUid: string) => void;
  selectedJobId?: string | null;
};

export const JobCardsWerker: React.FC<Props> = memo(
  ({ jobCardSearchText, segmentSelection, PaidStatus, viewOptions, onBeforeNavigate, onJobSelect, selectedJobId }) => {
    const { werkerJob, invitedFeedJobs, scheduledJobsCount, pastJobsCount } = useContext(WerkerJobContext);

    const history = useHistory();
    const { profile } = useContext(ProfileContext);

    const isPastSegment = segmentSelection === WERK_SEGMENT_SELECTOR.WerkerPast;
    const isInvitedSegment = segmentSelection === WERK_SEGMENT_SELECTOR.WerkerInvited;

    const effectiveViewOptions = viewOptions ?? { payment: "unpaid", status: "not_cancelled", sort: "smart" };

    // const isScheduledSegment = segmentSelection === WERK_SEGMENT_SELECTOR.WerkerScheduled;

    // IMPORTANT: Werker Available (Invited) is never sortable + ignores chip filters
    // const allowSort = !isInvitedSegment && (isScheduledSegment || isPastSegment);

    // Chips only make sense on Past (matching what you do on Scheduler side)
    const allowChips = isPastSegment;

    // Use shared filter utilities
    const matchesPaidChipFilter = createWerkerPaymentFilter(effectiveViewOptions.payment, allowChips);
    const matchesStatusChipFilter = createWerkerStatusFilter(effectiveViewOptions.status, allowChips);

    // Using imported utilities for getStartTs, getFinishTs, getHoursText, getCreatedTs

    const getDayOfWeekDateText = (job: any) => {
      const raw = job.StartDateTimeRaw ?? job.startDateTimeRaw;
      if (!raw) return job.DateDay ?? "";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return job.DateDay ?? "";
      return format(d, "EEE • M/d/yy"); // Wed 2/18/26
    };

    const getFinishDayOfWeekDateText = (job: any) => {
      const raw = job.FinishDateTimeRaw ?? job.finishDateTimeRaw;
      if (!raw) return getDayOfWeekDateText(job);
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return getDayOfWeekDateText(job);
      return format(d, "EEE • M/d/yy");
    };

    const getNotesPreview = (notesRaw: any, maxChars: number = 30): string => {
      if (!notesRaw) return "";

      const notes = String(notesRaw)
        .replace(/\r\n|\r|\n/g, " ") // newlines -> spaces
        .replace(/\s+/g, " ") // collapse whitespace
        .trim();

      if (!notes) return "";

      if (notes.length <= maxChars) return notes;

      // Prefer cutting at a word boundary when possible
      const slice = notes.slice(0, maxChars);
      const lastSpace = slice.lastIndexOf(" ");
      const safeCut = lastSpace > Math.floor(maxChars * 0.6) ? slice.slice(0, lastSpace) : slice;

      return `${safeCut}…`;
    };

    const handleClearOneBadge = async (isReadBool: boolean, myJobDocId: string, jobId: string) => {
      if (isReadBool === true) return;

      if (myJobDocId) {
        const seenJobRef = doc(db, FB_COLLECTION_PATH.JobsDocs, myJobDocId);
        await updateDoc(seenJobRef, { "jobInfo.status.isRead": true });
      }

      if (profile?.UserId && jobId) {
        const feedRef = doc(db, FB_COLLECTION_PATH.Users, profile.UserId, FB_COLLECTION_PATH.JobFeed, jobId);
        await setDoc(feedRef, { isRead: true }, { merge: true });
      }
    };

    const dismissGhost = async (jobId: string) => {
      if (!profile?.UserId || !jobId) return;

      const feedRef = doc(db, FB_COLLECTION_PATH.Users, profile.UserId, FB_COLLECTION_PATH.JobFeed, jobId);
      await setDoc(
        feedRef,
        {
          dismissed: true,
          dismissedAt: Date.now(),
        },
        { merge: true }
      );
    };

    const _routeToChat = async (jobChatId: string) => {
      if (onBeforeNavigate) {
        try {
          await onBeforeNavigate();
        } catch (e) {
          // ignore
        }
      }

      history.push({
        pathname: `/Chat/${jobChatId}`,
        state: { msgCat: "JobMsg" },
      });
    };

    // --- Filters (shared) ---
    const matchesPaidFilter = useCallback((job: any) => {
      if (PaidStatus === "") return true;
      return PaidStatus === job.PaidWerker;
    }, [PaidStatus]);

    // Use shared search filter utility
    const matchesSearch = createWerkerSearchFilter(jobCardSearchText);

    // --- Data sources by segment ---
    const legacyFilteredSorted = useMemo(() => {
      const base = (werkerJob || [])
        .filter((thisJob: any) => matchesPaidFilter(thisJob))
        .filter((job: any) => {
          if (segmentSelection === WERK_SEGMENT_SELECTOR.WerkerScheduled)
            return job.JobStatusWerker === WORKFLOW_WERKER_TYPES.Scheduled;
          if (segmentSelection === WERK_SEGMENT_SELECTOR.WerkerPast)
            return job.JobStatusWerker === WORKFLOW_WERKER_TYPES.Past;
          return false;
        })
        .filter(matchesSearch)
        // ✅ chips apply only in Past
        .filter((j: any) => matchesPaidChipFilter(j))
        .filter((j: any) => matchesStatusChipFilter(j));

      // Use shared sort utility
      const sorted = applySortMode(base, effectiveViewOptions.sort, isPastSegment);

      return sorted;
    }, [
      werkerJob,
      segmentSelection,
      isPastSegment,
      effectiveViewOptions.sort,
      matchesPaidChipFilter,
      matchesStatusChipFilter,
      matchesSearch,
      matchesPaidFilter,
    ]);

    const invitedFilteredSorted = useMemo(() => {
      const base = (invitedFeedJobs || [])
        .filter((j: any) => matchesPaidFilter(j))
        .filter(matchesSearch)
        .sort((a: any, b: any) => {
          // Soonest start time first
          const aT = getStartTs(a);
          const bT = getStartTs(b);
          if (aT !== bT) return aT - bT;

          // stable tie-breaker prevents random reshuffles
          return String(a.JobDocId).localeCompare(String(b.JobDocId));
        });

      return base;
    }, [invitedFeedJobs, matchesPaidFilter, matchesSearch]);

    // Display-only safe fallbacks (no wiring change)
    const getCompanyText = (job: any) => job.Company ?? job.company ?? job.Organization ?? job.organization ?? "";
    const getLocationText = (job: any) => job.Location ?? job.location ?? "";
    const _getLocation2Text = (job: any) =>
      job.LocationLine2 ?? job.locationLine2 ?? job.ShiftIdentifier ?? job.shiftIdentifier ?? "";

    const renderCard = (job: any, _index: number) => {
      const feedStatus = (job as any).__feedStatus; // "expired" | "cancelled" | undefined
      const isGhost = feedStatus === "expired" || feedStatus === "cancelled";
      const isCancelled = job.JobCancelledByScheduler === true || feedStatus === "cancelled";
      const isPaid = job.PaidWerker === true;

      // Rule: NO chat on invited/available/ghost
      const canShowChat = segmentSelection !== null && !isGhost;

      const unreadCountRaw =
        (job as any).UnreadCount ??
        (job as any).Unread ??
        (job as any).NewMessageCount ??
        (job as any).NewMessagesCount ??
        null;

      const unreadCount = typeof unreadCountRaw === "number" && Number.isFinite(unreadCountRaw) ? unreadCountRaw : null;

      const showUnreadBadge = canShowChat && job.IsNewMessage === true;

      const navToDetailsIfAllowed = async (jobArg: any) => {
        if (isGhost) {
          await dismissGhost(jobArg.JobDocId);
          return;
        }

        if (onBeforeNavigate) {
          try {
            await onBeforeNavigate();
          } catch (e) {
            // ignore
          }
        }

        await handleClearOneBadge(jobArg.IsRead, jobArg.MyJobDocId, jobArg.JobDocId);

        if (onJobSelect) {
          // Desktop mode: trigger selection handler
          onJobSelect(jobArg.MyJobDocId, jobArg.JobDocId, jobArg.SchedUid);
        } else {
          // Mobile mode: navigate to details page
          history.push(`/ShiftDetails/${jobArg.MyJobDocId}/${jobArg.JobDocId}/${jobArg.SchedUid}`);
        }
      };

      const companyText = getCompanyText(job);
      const locationText = getLocationText(job);

      const dayText = getDayOfWeekDateText(job);
      const finishDayText = getFinishDayOfWeekDateText(job);
      const hoursText = getHoursText(job);

      const timeLineLeft = `${dayText} • ${job.StartDateTime ?? ""}`;
      const endTimeLineLeft = `${finishDayText} • ${job.FinishDateTime ?? ""}${hoursText ? ` • ${hoursText}` : ""}`;

      const notesPreview = getNotesPreview(job.ShiftNotes ?? job.shiftNotes ?? "", 30);

      return (
        <IonCard
          // IMPORTANT: stable key only
          key={job.JobDocId}
          style={{
            margin: "12px 12px",
            borderRadius: "7px",
            overflow: "hidden",
            opacity: isGhost ? 0.55 : 1,
            position: "relative",
            height: "160px",
            border: selectedJobId === job.MyJobDocId ? "3px solid var(--ion-color-primary)" : undefined,
            backgroundColor: selectedJobId === job.MyJobDocId ? "var(--ion-color-light)" : undefined,
          }}
          className={job.IsRead === false && !isGhost ? "cardOutlineWerker" : ""}
        >
          {/* Keep the element always mounted; just toggle visibility */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%) rotate(-8deg)",
              fontSize: "30px",
              fontWeight: 900,
              letterSpacing: "2px",
              textTransform: "uppercase",
              pointerEvents: "none",
              zIndex: 3,
              whiteSpace: "nowrap",
              textShadow: "0 2px 6px rgba(0,0,0,0.35)",
              opacity: isGhost ? 1 : 0,
            }}
          >
            {feedStatus === "cancelled" ? "CANCELLED" : feedStatus === "expired" ? "EXPIRED" : ""}
          </div>

          <IonCardHeader
            style={{ padding: "10px 12px 6px" }}
          >
            <IonGrid
              fixed={true}
              style={{ padding: 0 }}
            >
              <IonRow
                class="ion-align-items-center"
              >
                <IonCol
                  size="auto"
                  style={{ padding: 0, paddingRight: "10px" }}
                >
                  <IonAvatar
                    style={{ width: "30px", height: "30px" }}
                    onClick={async () => {
                      await navToDetailsIfAllowed(job);
                    }}
                  >
                    <img alt="" src={job.SchedProfilePic ?? job.schedProfilePicUrl ?? ""} />
                  </IonAvatar>
                </IonCol>

                <IonCol
                  style={{ padding: 0, minWidth: 0 }}
                  onClick={async () => {
                    await navToDetailsIfAllowed(job);
                  }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontWeight: 800,
                      fontSize: "15px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {job.SchedFirstName ?? job.schedFirstName ?? ""} {job.SchedLastName ?? job.schedLastName ?? ""}
                  </IonText>
                </IonCol>

                <IonCol
                  size="auto"
                  style={{
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    position: "relative",
                  }}
                  onClick={(e) => {
                    // prevent accidental card click when tapping icons
                    e.stopPropagation();
                  }}
                >
                  {isCancelled && (
                    <IonIcon
                      icon={banOutline}
                      color="danger"
                      style={{ fontSize: "18px", opacity: 0.95 }}
                    />
                  )}

                  {isPaid && (
                    <IonIcon
                      icon={cashOutline}
                      color="success"
                      style={{ fontSize: "18px", opacity: 0.95 }}
                    />
                  )}

                  {/* Chat icon (and unread badge) */}
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <IonIcon
                      onClick={async () => {
                        // keep your existing behavior (currently routes to details via navToDetailsIfAllowed)
                        // If you want chat to open instead, we can swap this to routeToChat(job.JobDocId or chatId).
                        await navToDetailsIfAllowed(job);
                      }}
                      icon={chatboxOutline}
                      style={{ fontSize: "20px", paddingRight: "4px", opacity: 0.7 }}
                    />

                    {showUnreadBadge && (
                      <IonBadge
                        color="danger"
                        style={{
                          position: "absolute",
                          top: "-1px",
                          right: "-4px",
                          fontSize: "6px",
                          padding: "2px 6px",
                          borderRadius: "999px",
                        }}
                      >
                        {unreadCount != null ? String(unreadCount) : "1"}
                      </IonBadge>
                    )}
                  </div>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardHeader>

          <IonCardContent
            style={{
              padding: "0 12px 10px",
              height: "calc(160px - 50px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <IonGrid
              fixed={true}
              style={{ padding: 0 }}
            >
              {/* Line 1: time/hours/pay */}
              <IonRow
                style={{ paddingTop: "5px" }}
                onClick={async () => {
                  await navToDetailsIfAllowed(job);
                }}
              >
                <IonCol
                  style={{ padding: 0, minWidth: 0, flex: "1 1 55%" }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 400,
                      opacity: 0.95,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ fontSize: "10px", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>Start</span>
                    {timeLineLeft}
                  </IonText>
                  <IonText
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 400,
                      opacity: 0.95,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ fontSize: "10px", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>End</span>
                    {endTimeLineLeft}
                  </IonText>
                </IonCol>
                <IonCol
                  size="auto"
                  style={{
                    padding: 0,
                    paddingLeft: "10px",

                    // 👇 constrain pay so it can’t bulldoze the left side
                    maxWidth: "40%", // ~half the row (tweak 40–50%)
                    flex: "0 1 40%", // allow shrink, don’t force grow
                    minWidth: 0, // important for ellipsis to work in flex
                    textAlign: "right",
                  }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontWeight: 900,
                      fontSize: "13px",

                      // 👇 ellipsis when too long
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {job.Pay}
                  </IonText>
                </IonCol>
              </IonRow>

              {/* Line 2: company */}
              <IonRow
                onClick={async () => {
                  await navToDetailsIfAllowed(job);
                }}
              >
                <IonCol
                  style={{ padding: 0, marginTop: "2px" }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontWeight: 900,
                      fontSize: "14px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {companyText || " "}
                  </IonText>
                </IonCol>
              </IonRow>

              {/* Line 3: location line 1 */}
              <IonRow
                onClick={async () => {
                  await navToDetailsIfAllowed(job);
                }}
              >
                <IonCol
                  style={{ padding: 0, maxWidth: "80%" }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 500,
                      opacity: 0.92,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {locationText || " "}
                  </IonText>
                </IonCol>
              </IonRow>

              <IonRow
                onClick={async () => {
                  await navToDetailsIfAllowed(job);
                }}
              >
                <IonCol
                  style={{ padding: 0, maxWidth: "70%" }}
                >
                  <IonText
                    style={{
                      display: "block",
                      fontSize: "12px",
                      fontWeight: 500,
                      opacity: 0.78,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      // maxWidth: "75%", // ensure it doesn’t overflow the card
                    }}
                  >
                    <strong>Notes: </strong> {notesPreview || "no additional notes... "}
                  </IonText>
                </IonCol>
                <IonCol
                  size="auto"
                  style={{
                    padding: 0,
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <IonButton
                    fill="outline"
                    size="small"
                    color="medium"
                    style={{ margin: 0, height: "24px", fontWeight: 500 }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await navToDetailsIfAllowed(job);
                    }}
                  >
                    Details
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>
      );
    };

    const showInvitedEmpty = isInvitedSegment && invitedFilteredSorted.length === 0;

    return (
      <React.Fragment>
        {segmentSelection === WERK_SEGMENT_SELECTOR.WerkerInvited && showInvitedEmpty && (
          <EmptyMessage text="If you had any available jobs they'd be here..." />
        )}

        {segmentSelection === WERK_SEGMENT_SELECTOR.WerkerScheduled && scheduledJobsCount === 0 && (
          <EmptyMessage text="If you had any scheduled jobs they'd be here..." />
        )}

        {segmentSelection === WERK_SEGMENT_SELECTOR.WerkerPast && pastJobsCount === 0 && (
          <EmptyMessage text="If you had any past jobs they'd be here..." />
        )}

        {/* DEBUG banner (remove after testing) */}
        {/* <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "6px 10px",
            fontSize: "12px",
          }}
        >
          <div>JobCardsWerker mountId: {debugMountId}</div>
          <div>mountCount: {debugMountCount}</div>
          <div>scrollTop: {debugScrollTop ?? "?"}</div>
          <div>renders: {debugRenderCountRef.current}</div>

          <button
            style={{
              marginTop: "6px",
              fontSize: "12px",
              padding: "4px 8px",
            }}
            onClick={() => {
              // Try to read the scrollTop from the nearest scrollable ion-content.
              // This is "best effort" without touching Home.tsx.
              const ionContent = document.querySelector("ion-content");
              if (!ionContent) return;

              // ion-content uses a shadow root; the scroll element is internal.
              // But Ionic exposes a method on the element instance.
              (ionContent as any).getScrollElement?.().then((el: any) => {
                setDebugScrollTop(el?.scrollTop ?? null);
              });
            }}
          >
            Refresh scrollTop
          </button>
        </div> */}

        <IonList>
          {segmentSelection === WERK_SEGMENT_SELECTOR.WerkerInvited && (
            <>{invitedFilteredSorted.map((job: any, idx: number) => renderCard(job, idx))}</>
          )}

          {segmentSelection !== WERK_SEGMENT_SELECTOR.WerkerInvited &&
            legacyFilteredSorted.map((job: any, idx: number) => renderCard(job, idx))}
        </IonList>
      </React.Fragment>
    );
  }
);
